import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, ThumbsUp, MessageCircle, Share2, ChevronLeft, ChevronRight, Download, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getBackendUrl } from '../utils/getBackendUrl';

const API = `${getBackendUrl()}/api`;

const ClientPhotoViewer = ({ photos, index, photo, getImageUrl, onNavigate, onClose }) => {
  const [likesCount, setLikesCount] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const touchStartRef = useRef(null);

  useEffect(() => {
    if (!photo?.id) return;
    setShowComments(false);
    axios.get(`${API}/photos/${photo.id}/likes`)
      .then(res => {
        setLikesCount(res.data?.likes_count || 0);
        setIsLiked(res.data?.user_liked || false);
      })
      .catch(() => {});
    axios.get(`${API}/photos/${photo.id}/comments`)
      .then(res => setComments(Array.isArray(res.data) ? res.data : []))
      .catch(() => setComments([]));
  }, [photo?.id]);

  const handleLike = async () => {
    try {
      const res = await axios.post(`${API}/photos/${photo.id}/like`);
      setIsLiked(res.data.liked);
      setLikesCount(res.data.likes_count);
    } catch {
      toast.error('Failed to like photo');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || isSubmitting) return;
    try {
      setIsSubmitting(true);
      const res = await axios.post(`${API}/photos/${photo.id}/comment`, { comment: newComment.trim() });
      setComments(prev => [...prev, res.data]);
      setNewComment('');
    } catch {
      toast.error('Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShare = async () => {
    const url = getImageUrl(photo);
    if (navigator.share) {
      try {
        await navigator.share({ title: photo.description || 'Project photo', url });
        return;
      } catch { /* user cancelled */ }
    } else {
      const link = document.createElement('a');
      link.href = url;
      link.download = `photo-${photo.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
  };

  const handleTouchEnd = (e) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 2 && Date.now() - start.time < 600) {
      onNavigate(dx < 0 ? 'next' : 'prev');
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') onNavigate('next');
      else if (e.key === 'ArrowLeft') onNavigate('prev');
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onNavigate, onClose]);

  if (!photo) return null;

  const photoDate = new Date(photo.timestamp).toLocaleDateString('en-CA', {
    timeZone: 'America/Toronto', month: 'short', day: 'numeric',
    year: new Date(photo.timestamp).getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="client-photo-viewer">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2 z-10">
        <button onClick={onClose} className="p-2 -ml-2 text-white" data-testid="client-viewer-close">
          <X className="w-7 h-7" strokeWidth={2.5} />
        </button>
        <span className="text-white font-bold text-xl" data-testid="client-viewer-counter">
          {index + 1} of {photos.length}
        </span>
        <button onClick={handleShare} className="p-2 -mr-2 text-white" data-testid="client-viewer-download">
          <Download className="w-6 h-6" />
        </button>
      </div>

      {/* Photo */}
      <div
        className="flex-1 flex items-center justify-center min-h-0 relative"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={getImageUrl(photo)}
          alt={photo.description || 'Project photo'}
          className="max-w-full max-h-full object-contain"
        />
        {photos.length > 1 && (
          <>
            <button
              onClick={() => onNavigate('prev')}
              className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors"
              data-testid="client-viewer-prev"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => onNavigate('next')}
              className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors"
              data-testid="client-viewer-next"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>

      {/* Bottom info + actions */}
      <div className="px-5 pb-8 pt-3 z-10">
        <p className="text-white text-2xl font-extrabold leading-tight" data-testid="client-viewer-uploader">
          {photo.uploaded_by_name}
        </p>
        <p className="text-gray-400 text-sm mt-0.5">
          {photoDate}{photo.description ? ` · ${photo.description}` : ''}
        </p>
        <div className="flex items-center gap-10 mt-5">
          <button
            onClick={handleLike}
            className="flex items-center gap-2 text-white"
            data-testid="client-viewer-like-btn"
          >
            <ThumbsUp className={`w-7 h-7 ${isLiked ? 'fill-blue-500 text-blue-500' : ''}`} strokeWidth={2} />
            {likesCount > 0 && <span className="text-lg font-bold">{likesCount}</span>}
          </button>
          <button
            onClick={() => setShowComments(true)}
            className="flex items-center gap-2 text-white"
            data-testid="client-viewer-comment-btn"
          >
            <MessageCircle className="w-7 h-7" strokeWidth={2} />
            {comments.length > 0 && <span className="text-lg font-bold">{comments.length}</span>}
          </button>
          <button onClick={handleShare} className="text-white" data-testid="client-viewer-share-btn">
            <Share2 className="w-7 h-7" strokeWidth={2} />
          </button>
        </div>
      </div>

      {/* Comments sheet */}
      {showComments && (
        <div className="absolute inset-0 z-20 flex flex-col justify-end" data-testid="client-viewer-comments-sheet">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowComments(false)} />
          <div className="relative bg-slate-900 rounded-t-2xl max-h-[70%] flex flex-col">
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <h3 className="text-white font-bold text-lg">Comments</h3>
              <button onClick={() => setShowComments(false)} className="p-2 text-gray-400" data-testid="client-viewer-comments-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 pb-3 space-y-3">
              {comments.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-6">No comments yet — be the first!</p>
              ) : (
                comments.map((c) => (
                  <div key={c.id} className="flex gap-2">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">
                      {c.user_name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="bg-slate-800 rounded-2xl px-3 py-2">
                        <p className="text-sm font-semibold text-white">{c.user_name}</p>
                        <p className="text-sm text-gray-300">{c.comment}</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 ml-3">
                        {new Date(c.created_at).toLocaleDateString('en-US', {
                          timeZone: 'America/Toronto', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="flex gap-2 items-end px-4 py-3 border-t border-slate-800" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
              <textarea
                placeholder="Write a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAddComment();
                  }
                }}
                rows={1}
                className="flex-1 bg-slate-800 text-white text-sm rounded-full px-4 py-3 resize-none outline-none placeholder:text-gray-500"
                data-testid="client-viewer-comment-input"
              />
              <button
                onClick={handleAddComment}
                disabled={!newComment.trim() || isSubmitting}
                className="p-3 rounded-full bg-blue-600 text-white disabled:opacity-40"
                data-testid="client-viewer-comment-send"
              >
                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default ClientPhotoViewer;
